import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  GraduationCap, LogOut, Settings, User, Building2, School, Users, 
  Activity, FileText, ClipboardList, Sparkles, ChevronRight, 
  Menu, X, LayoutDashboard, Search, Bell, Package, Grid3X3,
  TrendingUp, Calendar, BookOpen, Shield, MessageSquare, BarChart3
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

import { API_URL } from '../utils/api';

// Dynamic icon mapping for extensible products
const ICON_MAP = {
  'activity': Activity,
  'file-text': FileText,
  'clipboard-list': ClipboardList,
  'package': Package,
  'trending-up': TrendingUp,
  'calendar': Calendar,
  'book-open': BookOpen,
  'shield': Shield,
  'message-square': MessageSquare,
  'bar-chart': BarChart3,
  'users': Users,
  'grid': Grid3X3,
};

// Category labels
const CATEGORY_LABELS = {
  'compliance': 'Compliance & Documentation',
  'intervention': 'Intervention & Progress',
  'practice': 'Daily Practice',
  'assessment': 'Assessment & Evaluation',
  'reporting': 'Reporting & Analytics',
  'communication': 'Communication',
  'scheduling': 'Scheduling',
  'documentation': 'Documentation',
  'other': 'Other Tools'
};

// Status badge styles
const STATUS_STYLES = {
  'active': { bg: 'bg-green-100', text: 'text-green-700', label: 'Active' },
  'beta': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Beta' },
  'coming_soon': { bg: 'bg-[#E2DACD]', text: 'text-[#6B5D4D]', label: 'Coming Soon' },
  'internal': { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Internal' },
  'maintenance': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Maintenance' }
};

export function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [productData, setProductData] = useState({ accessible_products: [], available_addons: [] });
  const [stats, setStats] = useState({ organizations: 0, schools: 0, students: 0, users: 0 });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeProductTab, setActiveProductTab] = useState('all');
  const [aiStatus, setAiStatus] = useState({ available: false });

  useEffect(() => {
    fetchProducts();
    fetchStats();
    fetchAIStatus();
  }, []);

  const fetchProducts = async () => {
    try {
      // Use the user-accessible products endpoint for proper permission filtering
      const response = await axios.get(`${API_URL}/api/products/user/accessible`, { withCredentials: true });
      setProductData({
        accessible_products: response.data.accessible_products || response.data.products || [],
        available_addons: response.data.available_addons || [],
        access_level: response.data.access_level
      });
    } catch (error) {
      // Fallback to public product list
      try {
        const fallbackRes = await axios.get(`${API_URL}/api/products`, { withCredentials: true });
        setProductData({
          accessible_products: fallbackRes.data || [],
          available_addons: [],
          access_level: 'public'
        });
      } catch (e) {
        console.error('Error fetching products:', e);
      }
    }
  };

  const fetchStats = async () => {
    try {
      const [orgsRes, schoolsRes, studentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/organizations`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/schools`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/students`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setStats({
        organizations: orgsRes.data.length,
        schools: schoolsRes.data.length,
        students: studentsRes.data.length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAIStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ai/status`, { withCredentials: true });
      setAiStatus(response.data);
    } catch (error) {
      console.error('Error fetching AI status:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getProductIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || Package;
    return IconComponent;
  };

  const getStatusStyle = (status) => {
    return STATUS_STYLES[status] || STATUS_STYLES['coming_soon'];
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      'admin': 'bg-[#2C4C3B]/10 text-[#2C4C3B]',
      'case_manager': 'bg-[#A7C7E7]/20 text-[#3B6A9B]',
      'teacher': 'bg-[#A7C7E7]/20 text-[#3B6A9B]',
      'therapist': 'bg-[#C2D3C0]/30 text-[#3A5A3A]',
      'parent': 'bg-[#E2DACD]/50 text-[#6B5D4D]',
      'viewer': 'bg-[#F3F4F1] text-[#595959]'
    };
    return classes[role] || classes['viewer'];
  };

  // Group products by category
  const groupProductsByCategory = (products) => {
    const grouped = {};
    products.forEach(p => {
      const cat = p.category || 'other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(p);
    });
    return grouped;
  };

  // Get all products and filter by tab
  const allProducts = [...productData.accessible_products];
  const filteredProducts = activeProductTab === 'all' 
    ? allProducts 
    : allProducts.filter(p => p.category === activeProductTab);

  // Get unique categories from products
  const categories = [...new Set(allProducts.map(p => p.category || 'other'))];

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', href: '/dashboard', active: location.pathname === '/dashboard' },
    { icon: Building2, label: 'Organizations', href: '/organizations', active: location.pathname === '/organizations' },
    { icon: School, label: 'Schools', href: '/schools', active: location.pathname === '/schools' },
    { icon: Users, label: 'Students', href: '/students', active: location.pathname === '/students' },
    { icon: User, label: 'Users', href: '/users', adminOnly: true, active: location.pathname === '/users' },
    { icon: Sparkles, label: 'AI Services', href: '/ai-playground', active: location.pathname === '/ai-playground' },
    { icon: Settings, label: 'Settings', href: '/settings', active: location.pathname === '/settings' },
  ];

  const userInitials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      {/* Top Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-[#E5E5E5]">
        <div className="px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-[#F3F4F1]"
                data-testid="mobile-menu-btn"
              >
                {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
              <Link to="/" className="flex items-center gap-2" data-testid="dashboard-logo">
                <GraduationCap className="h-7 w-7 text-[#2C4C3B]" />
                <span className="font-outfit text-xl font-semibold text-[#1C1C1C] hidden sm:block">FrameEd</span>
              </Link>
            </div>

            <div className="flex-1 max-w-xl mx-8 hidden md:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#595959]" />
                <input
                  type="text"
                  placeholder="Search students, schools, products..."
                  className="w-full pl-10 pr-4 py-2 bg-[#F3F4F1] border-none rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
                  data-testid="search-input"
                />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button className="p-2 rounded-lg hover:bg-[#F3F4F1] relative" data-testid="notifications-btn">
                <Bell className="h-5 w-5 text-[#595959]" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#2C4C3B] rounded-full" />
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-3 p-1 rounded-xl hover:bg-[#F3F4F1]" data-testid="user-menu-btn">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-[#2C4C3B] text-white text-sm">{userInitials}</AvatarFallback>
                    </Avatar>
                    <div className="hidden sm:block text-left">
                      <p className="text-sm font-medium text-[#1C1C1C]">{user?.name}</p>
                      <p className="text-xs text-[#595959]">{user?.email}</p>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-[#E5E5E5] transition-transform duration-200 ease-in-out pt-16 lg:pt-0`}>
          <div className="p-6">
            <nav className="space-y-1">
              {navItems.map((item) => {
                if (item.adminOnly && user?.role !== 'admin') return null;
                return (
                  <Link
                    key={item.label}
                    to={item.href}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                      item.active
                        ? 'bg-[#2C4C3B] text-white'
                        : 'text-[#595959] hover:bg-[#F3F4F1] hover:text-[#1C1C1C]'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* Products Quick Access */}
            {allProducts.length > 0 && (
              <div className="mt-8 pt-6 border-t border-[#E5E5E5]">
                <p className="px-4 mb-3 text-xs font-semibold text-[#595959] uppercase tracking-wider">Products</p>
                <nav className="space-y-1">
                  {allProducts.slice(0, 5).map((product) => {
                    const Icon = getProductIcon(product.icon);
                    const status = getStatusStyle(product.status);
                    const isLaunchable = product.status === 'active' && product.launch_route;
                    const Wrapper = isLaunchable ? Link : 'div';
                    const wrapperProps = isLaunchable ? { to: product.launch_route } : {};
                    return (
                      <Wrapper
                        key={product.product_id}
                        {...wrapperProps}
                        className="flex items-center gap-3 px-4 py-2 rounded-xl text-sm text-[#595959] hover:bg-[#F3F4F1] cursor-pointer"
                        data-testid={`sidebar-product-${product.product_id}`}
                      >
                        <Icon className="h-4 w-4" style={{ color: product.color }} />
                        <span className="flex-1 truncate">{product.display_name || product.name}</span>
                        {product.status === 'active' ? (
                          <ChevronRight className="h-3 w-3 text-[#595959]" />
                        ) : (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${status.bg} ${status.text}`}>
                            {status.label === 'Coming Soon' ? 'Soon' : status.label}
                          </span>
                        )}
                      </Wrapper>
                    );
                  })}
                </nav>
              </div>
            )}
          </div>
        </aside>

        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/20 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8" data-testid="dashboard-content">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-[#1C1C1C] mb-2">
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <div className="flex items-center gap-2">
              <p className="text-[#595959]">You're signed in as</p>
              <Badge className={`${getRoleBadgeClass(user?.role)} text-xs font-semibold uppercase`}>
                {user?.role?.replace('_', ' ')}
              </Badge>
              {productData.access_level && productData.access_level !== 'none' && (
                <Badge variant="outline" className="text-xs">
                  {productData.access_level} access
                </Badge>
              )}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Link to="/organizations" className="bg-white border border-[#E5E5E5] rounded-2xl p-6 hover:shadow-lg transition-shadow" data-testid="stat-organizations">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#2C4C3B]/10 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-[#2C4C3B]" />
                </div>
                <span className="text-xs font-medium text-[#595959]">Total</span>
              </div>
              <p className="font-outfit text-3xl font-semibold text-[#1C1C1C]">{stats.organizations}</p>
              <p className="text-sm text-[#595959] mt-1">Organizations</p>
            </Link>

            <Link to="/schools" className="bg-white border border-[#E5E5E5] rounded-2xl p-6 hover:shadow-lg transition-shadow" data-testid="stat-schools">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#A7C7E7]/20 flex items-center justify-center">
                  <School className="h-5 w-5 text-[#3B6A9B]" />
                </div>
                <span className="text-xs font-medium text-[#595959]">Total</span>
              </div>
              <p className="font-outfit text-3xl font-semibold text-[#1C1C1C]">{stats.schools}</p>
              <p className="text-sm text-[#595959] mt-1">Schools</p>
            </Link>

            <Link to="/students" className="bg-white border border-[#E5E5E5] rounded-2xl p-6 hover:shadow-lg transition-shadow" data-testid="stat-students">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#C2D3C0]/30 flex items-center justify-center">
                  <Users className="h-5 w-5 text-[#3A5A3A]" />
                </div>
                <span className="text-xs font-medium text-[#595959]">Active</span>
              </div>
              <p className="font-outfit text-3xl font-semibold text-[#1C1C1C]">{stats.students}</p>
              <p className="text-sm text-[#595959] mt-1">Students</p>
            </Link>

            <Link to="/ai-playground" className="bg-white border border-[#E5E5E5] rounded-2xl p-6 hover:shadow-lg transition-shadow" data-testid="stat-ai">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#E2DACD]/50 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-[#6B5D4D]" />
                </div>
                <span className={`text-xs font-medium ${aiStatus.available ? 'text-[#2C4C3B]' : 'text-[#595959]'}`}>
                  {aiStatus.available ? 'Active' : 'Setup'}
                </span>
              </div>
              <p className="font-outfit text-3xl font-semibold text-[#1C1C1C]">{aiStatus.service_count || 'AI'}</p>
              <p className="text-sm text-[#595959] mt-1">AI Services</p>
            </Link>
          </div>

          {/* Products Section - Extensible */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]">FrameEd Products</h2>
                <p className="text-sm text-[#595959] mt-1">
                  {allProducts.length} product{allProducts.length !== 1 ? 's' : ''} available
                  {productData.available_addons?.length > 0 && ` · ${productData.available_addons.length} add-on${productData.available_addons.length !== 1 ? 's' : ''}`}
                </p>
              </div>
            </div>

            {/* Category Tabs */}
            {categories.length > 1 && (
              <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setActiveProductTab('all')}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                      activeProductTab === 'all'
                        ? 'bg-[#2C4C3B] text-white'
                        : 'bg-[#F3F4F1] text-[#595959] hover:bg-[#E5E5E5]'
                    }`}
                    data-testid="product-tab-all"
                  >
                    All Products
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setActiveProductTab(cat)}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                        activeProductTab === cat
                          ? 'bg-[#2C4C3B] text-white'
                          : 'bg-[#F3F4F1] text-[#595959] hover:bg-[#E5E5E5]'
                      }`}
                      data-testid={`product-tab-${cat}`}
                    >
                      {CATEGORY_LABELS[cat] || cat}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Products Grid - Scalable */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredProducts.map((product) => {
                const Icon = getProductIcon(product.icon);
                const status = getStatusStyle(product.status);
                return (
                  <div
                    key={product.product_id}
                    className="bg-white border border-[#E5E5E5] rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex flex-col"
                    data-testid={`dashboard-product-${product.product_id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${product.color}15` }}
                      >
                        <Icon className="h-6 w-6" style={{ color: product.color }} />
                      </div>
                      <Badge className={`${status.bg} ${status.text} text-xs`}>
                        {status.label}
                      </Badge>
                    </div>
                    <h3 className="font-outfit text-lg font-semibold text-[#1C1C1C] mb-2">
                      {product.display_name || product.name}
                    </h3>
                    <p className="text-sm text-[#595959] mb-4 line-clamp-2 flex-1">
                      {product.short_description || product.description}
                    </p>
                    {product.features && product.features.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {product.features.slice(0, 3).map((feature) => (
                          <span key={feature} className="text-xs px-2 py-1 bg-[#F3F4F1] rounded-lg text-[#595959]">
                            {feature}
                          </span>
                        ))}
                        {product.features.length > 3 && (
                          <span className="text-xs px-2 py-1 bg-[#F3F4F1] rounded-lg text-[#595959]">
                            +{product.features.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                    {product.status === 'active' && product.launch_route && (
                      <Link to={product.launch_route}>
                        <Button variant="outline" className="w-full rounded-xl border-[#E5E5E5] hover:border-[#2C4C3B]">
                          Launch
                          <ChevronRight className="h-4 w-4 ml-2" />
                        </Button>
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Available Add-ons */}
            {productData.available_addons?.length > 0 && (
              <div className="mt-8 pt-8 border-t border-[#E5E5E5]">
                <h3 className="font-outfit text-lg font-semibold text-[#1C1C1C] mb-4">Available Add-ons</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {productData.available_addons.map((addon) => {
                    const Icon = getProductIcon(addon.icon);
                    return (
                      <div
                        key={addon.product_id}
                        className="bg-[#F3F4F1] border border-dashed border-[#E5E5E5] rounded-2xl p-4 opacity-75"
                        data-testid={`addon-${addon.product_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-white">
                            <Icon className="h-5 w-5" style={{ color: addon.color }} />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-[#1C1C1C] text-sm">{addon.display_name || addon.name}</h4>
                            <p className="text-xs text-[#595959]">Contact admin to enable</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div>
            <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C] mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/organizations" data-testid="quick-action-org">
                <Button variant="outline" className="w-full h-auto py-4 px-6 rounded-xl border-[#E5E5E5] hover:border-[#2C4C3B] hover:bg-[#2C4C3B]/5 justify-start">
                  <Building2 className="h-5 w-5 mr-3 text-[#2C4C3B]" />
                  <span className="text-[#1C1C1C]">Add Organization</span>
                </Button>
              </Link>
              <Link to="/schools" data-testid="quick-action-school">
                <Button variant="outline" className="w-full h-auto py-4 px-6 rounded-xl border-[#E5E5E5] hover:border-[#2C4C3B] hover:bg-[#2C4C3B]/5 justify-start">
                  <School className="h-5 w-5 mr-3 text-[#3B6A9B]" />
                  <span className="text-[#1C1C1C]">Add School</span>
                </Button>
              </Link>
              <Link to="/students" data-testid="quick-action-student">
                <Button variant="outline" className="w-full h-auto py-4 px-6 rounded-xl border-[#E5E5E5] hover:border-[#2C4C3B] hover:bg-[#2C4C3B]/5 justify-start">
                  <Users className="h-5 w-5 mr-3 text-[#3A5A3A]" />
                  <span className="text-[#1C1C1C]">Add Student</span>
                </Button>
              </Link>
              <Link to="/ai-playground" data-testid="quick-action-ai">
                <Button variant="outline" className="w-full h-auto py-4 px-6 rounded-xl border-[#E5E5E5] hover:border-[#2C4C3B] hover:bg-[#2C4C3B]/5 justify-start">
                  <Sparkles className="h-5 w-5 mr-3 text-[#6B5D4D]" />
                  <span className="text-[#1C1C1C]">AI Services</span>
                </Button>
              </Link>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
